import { useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useStores } from '../../../stores/useStores';
import type { EllipseCrownOrbitAnimationRef } from '../../EllipseOrbitAnimation/EllipseOrbitAnimation';
import { EllipseOrbitAnimation } from '../../EllipseOrbitAnimation/EllipseOrbitAnimation';
import { getSlideOrder, HOME_PAGE_ORDER } from '../../FullPageDialogs/config';
import type { HornsAnimationRef } from '../../HornsAnimation/HornsAnimation';
import { HornsAnimation } from '../../HornsAnimation/HornsAnimation';
import Crown from '../../SVGIcons/Crown';

import styles from './FooterNavigation.module.scss';
import { getFooterColors } from './getFooterColors';
import { LobbyTabLine } from './LobbyTabLine';
import { RegularTabLine } from './RegularTabLine';

const TABS_COUNT = 5;

export const FooterAnimations = observer(function FooterAnimations() {
  const currentDialogOrder = useRef(0);
  const shifterRef = useRef<HTMLDivElement | null>(null);
  const ellipseCrownOrbitRef = useRef<EllipseCrownOrbitAnimationRef>(null);
  const hornsAnimationRef = useRef<HornsAnimationRef>(null);

  const { uiStore, coinTypeStore } = useStores();

  useEffect(() => {
    const slideOrder = getSlideOrder(uiStore.openFullPageDialog);
    const isHomeTabActive = slideOrder === HOME_PAGE_ORDER;

    if (currentDialogOrder.current === slideOrder) {
      return;
    }

    if (shifterRef.current) {
      shifterRef.current.style.setProperty('--orderTranslateX', (TABS_COUNT + 1 - slideOrder) * -100 + '%');

      shifterRef.current.style.setProperty('--lobbyTabLineOpacity', isHomeTabActive ? '1' : '0');
      shifterRef.current.style.setProperty('--regularTabLineOpacity', isHomeTabActive ? '0' : '1');
    }
    void ellipseCrownOrbitRef.current?.start(slideOrder >= currentDialogOrder.current ? 'right' : 'left');
    if (isHomeTabActive) {
      void hornsAnimationRef.current?.start();
    } else {
      void hornsAnimationRef.current?.hide();
    }
    currentDialogOrder.current = slideOrder;
  }, [uiStore.openFullPageDialog]);

  const lineColor = useMemo(() => {
    const { mainColor } = getFooterColors(coinTypeStore.coinType);
    return mainColor;
  }, [coinTypeStore.coinType]);

  return (
    <div className={styles.animationsContainer}>
      <div ref={shifterRef} className={styles.footerNavigationShifter}>
        <div className={styles.footerNavigationShifterArrow}>
          <div className={styles.regularTabLine}>
            <EllipseOrbitAnimation
              ref={ellipseCrownOrbitRef}
              innerClassName={styles.ellipseCrownOrbitInner}
              wrapperClassName={styles.ellipseCrownOrbit}
            >
              <Crown />
            </EllipseOrbitAnimation>
            <RegularTabLine className={styles.tabLine} mainColor={lineColor} />
          </div>
          <div className={styles.lobbyTabLine}>
            <div className={styles.hornsAnimationWrapper}>
              <HornsAnimation ref={hornsAnimationRef} shown>
                <Crown />
              </HornsAnimation>
            </div>
            <LobbyTabLine className={classNames(styles.tabLine, styles.lobbyTabLine)} mainColor={lineColor} />
          </div>
        </div>
      </div>
    </div>
  );
});
